import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { groupPortfoliosByType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import { validateKeywordsSize, MAX_KEYWORDS_COUNT, MAX_KEYWORDS_TOTAL_CHARS } from "@/pages/PortfolioBuilder/keywordLimits";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications, type FilterNotifications } from "@/types/notifications";
import toast from "react-hot-toast";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import * as Sentry from "@sentry/react";

type Category = { title: string; id: number | null };

export interface UseFilterBuilderOptions {
  queryId?: number | null;
  /** Pre-fill for CREATE mode (concierge "describe it" flow) — seeds the
   *  form without entering edit mode; saving still creates a new filter. */
  draft?: {
    name?: string;
    searchKeyword?: string;
    tags?: string[];
    filterMode?: "AND" | "OR";
    keywordOp?: "AND" | "OR";
  } | null;
  onSaved?: (queryName: string, categories: string[]) => void;
  onSaveFailed?: () => void;
  onDeleted?: () => void;
}

export function useFilterBuilder(options: UseFilterBuilderOptions) {
  const confirm = useConfirm();
  const { queriesQuery, createMutation, editMutation, deleteMutation } = useHeadlineQueriesCRUD();
  const { portfolios } = usePortfoliosCRUD();
  const { watchlistsQuery: tagsWatchlistsQuery } = useTagsWatchlistsCRUD();
  const tagsWatchlists = tagsWatchlistsQuery.data || [];
  const headlineQueries = queriesQuery.data;

  // ── Form state ────────────────────────────────────────────────────────
  const [queryName, setQueryName] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedTagsWatchlistIds, setSelectedTagsWatchlistIds] = useState<number[]>([]);
  const [filterMode, setFilterMode] = useState<"AND" | "OR">("OR");
  const [keywordOp, setKeywordOp] = useState<"AND" | "OR">("OR");
  const [keywordWatchlistOp, setKeywordWatchlistOp] = useState<"AND" | "OR">("OR");
  const [notifications, setNotifications] = useState<FilterNotifications>(emptyNotifications());
  const [isDeleting, setIsDeleting] = useState(false);

  const isEditing = options.queryId != null;

  // ── Portfolio grouping ────────────────────────────────────────────────
  const selectedGrouped = useMemo(() => {
    const selected = portfolios.filter((p) => selectedPortfolioIds.includes(p.id));
    return groupPortfoliosByType(selected);
  }, [portfolios, selectedPortfolioIds]);

  const hasBothWatchlistTypes =
    selectedGrouped.tickerOnly.length > 0 && selectedGrouped.keywordOnly.length > 0;

  useEffect(() => {
    if (hasBothWatchlistTypes && keywordOp === "OR") {
      setKeywordOp("AND");
    }
  }, [hasBothWatchlistTypes]);

  // Resolve selected tag watchlist objects
  const selectedTagWatchlistObjects = useMemo(
    () => tagsWatchlists.filter((tw) => selectedTagsWatchlistIds.includes(tw.id)),
    [tagsWatchlists, selectedTagsWatchlistIds]
  );

  // ── Keyword validation ────────────────────────────────────────────────
  const aggregatedKeywords = useMemo(() => {
    const fromWatchlists = selectedGrouped.keywordOnly.flatMap((p) => p.keywords ?? []);
    const typed = searchKeyword.trim();
    return typed ? [...fromWatchlists, typed] : fromWatchlists;
  }, [selectedGrouped.keywordOnly, searchKeyword]);

  const keywordsSize = validateKeywordsSize(aggregatedKeywords);

  const hasKeywords = searchKeyword.trim().length > 0 || selectedGrouped.keywordOnly.length > 0;
  const hasTickerWatchlists = selectedGrouped.tickerOnly.length > 0;
  const hasTags = selectedCategories.length > 0 || selectedTagsWatchlistIds.length > 0;
  const hasWatchlistsOrTags = hasTickerWatchlists || hasTags;

  // ── Counts per type ───────────────────────────────────────────────────
  const tickerCount = selectedGrouped.tickerOnly.length;
  const keywordCount = selectedGrouped.keywordOnly.length + (searchKeyword.trim() ? 1 : 0);
  const tagCount = selectedCategories.length + selectedTagsWatchlistIds.length;

  // ── Load existing query for editing ───────────────────────────────────
  useEffect(() => {
    if (!isEditing || !headlineQueries) return;
    const q = headlineQueries.find((query) => query.id === options.queryId);
    if (!q) return;

    setQueryName(q.queryName);
    setSearchKeyword(q.searchKeyword ?? "");
    setSelectedPortfolioIds(q.portfolioIds ?? []);
    setSelectedTagsWatchlistIds(q.tagsWatchlistIds ?? []);
    setFilterMode(q.filterMode ?? "OR");
    setKeywordOp(q.keywordOp ?? "OR");
    setKeywordWatchlistOp(q.keywordWatchlistOp ?? "OR");
    // Keep any extra backend sources from the API — PATCH replaces the whole map
    setNotifications(q.notifications ?? emptyNotifications());

    // Convert category IDs back to Category objects
    const cats: Category[] = (q.categories ?? [])
      .map((catId) =>
        menuCategories.flatMap((c) => c.items).find((item) => item.id === Number(catId))
      )
      .filter((item): item is { title: string; id: number } => !!item);
    setSelectedCategories(cats);
  }, [isEditing, options.queryId, headlineQueries]);

  // ── Seed from a concierge draft (create mode only) ────────────────────
  useEffect(() => {
    const draft = options.draft;
    if (!draft || isEditing) return;
    setQueryName(draft.name ?? "");
    setSearchKeyword(draft.searchKeyword ?? "");
    if (draft.filterMode) setFilterMode(draft.filterMode);
    if (draft.keywordOp) setKeywordOp(draft.keywordOp);
    const cats: Category[] = (draft.tags ?? [])
      .map((catId) =>
        menuCategories.flatMap((c) => c.items).find((item) => item.id === Number(catId))
      )
      .filter((item): item is { title: string; id: number } => !!item);
    setSelectedCategories(cats);
  }, [options.draft, isEditing]);

  // ── Toggle helpers ────────────────────────────────────────────────────
  const togglePortfolio = useCallback((id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleCategory = useCallback((cat: Category) => {
    setSelectedCategories((prev) => {
      const exists = prev.some((c) => c.id === cat.id);
      return exists ? prev.filter((c) => c.id !== cat.id) : [...prev, cat];
    });
  }, []);

  const toggleTagWatchlist = useCallback((id: number) => {
    setSelectedTagsWatchlistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const clearAll = useCallback(() => {
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
  }, []);

  const reset = useCallback(() => {
    setQueryName("");
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setNotifications(emptyNotifications());
    setFormErrors({ categories: "", keywords: "" });
  }, []);

  // ── Semantic summary ──────────────────────────────────────────────────
  const semanticSummary = useMemo(() => {
    const parts: string[] = [];

    // Keywords
    const kwParts: string[] = [];
    if (searchKeyword.trim()) kwParts.push(`"${searchKeyword.trim()}"`);
    selectedGrouped.keywordOnly.forEach((p) => kwParts.push(p.name));
    if (kwParts.length > 0) {
      const joiner = keywordWatchlistOp === "AND" ? " and " : " or ";
      parts.push(`containing ${kwParts.join(joiner)}`);
    }

    // Tickers
    if (selectedGrouped.tickerOnly.length > 0) {
      const names = selectedGrouped.tickerOnly.map((p) => p.name);
      parts.push(`mentioning ${names.join(" or ")} tickers`);
    }

    // Tags
    const tagParts: string[] = [];
    if (selectedCategories.length > 0) tagParts.push(...selectedCategories.map((c) => c.title));
    if (selectedTagWatchlistObjects.length > 0) tagParts.push(...selectedTagWatchlistObjects.map((tw) => tw.name));
    if (tagParts.length > 0) {
      parts.push(`tagged ${tagParts.join(" or ")}`);
    }

    if (parts.length === 0) return "No filters selected";
    const joiner = keywordOp === "AND" ? " AND " : " OR ";
    return "Headlines " + parts.join(joiner);
  }, [searchKeyword, selectedGrouped, selectedCategories, selectedTagWatchlistObjects, keywordOp, keywordWatchlistOp]);

  // ── Submit ────────────────────────────────────────────────────────────
  const [formErrors, setFormErrors] = useState({ categories: "", keywords: "" });

  const handleSubmit = useCallback(async () => {
    setFormErrors({ categories: "", keywords: "" });

    if (
      selectedCategories.length === 0 &&
      selectedPortfolioIds.length === 0 &&
      selectedTagsWatchlistIds.length === 0 &&
      searchKeyword.trim() === ""
    ) {
      setFormErrors((prev) => ({
        ...prev,
        categories: "Select at least one: keywords, watchlist, or tags.",
      }));
      return;
    }

    if (hasKeywords && !keywordsSize.ok) {
      setFormErrors((prev) => ({
        ...prev,
        keywords: keywordsSize.error ?? "Keyword limit exceeded.",
      }));
      return;
    }

    const payload = {
      queryName,
      categories: selectedCategories.map((cat) => String(cat.id ?? "")),
      searchKeyword,
      portfolioIds: selectedPortfolioIds,
      tagsWatchlistIds: selectedTagsWatchlistIds,
      filterMode,
      keywordOp,
      keywordWatchlistOp,
      notifications,
    };

    try {
      if (isEditing) {
        await editMutation.mutateAsync({ id: options.queryId as number, ...payload });
        toast.success("Filter updated");
      } else {
        await createMutation.mutateAsync({ id: undefined, ...payload });
        toast.success("Filter created");
      }
      options.onSaved?.(payload.queryName, payload.categories);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to save filter");
      options.onSaveFailed?.();
    }
  }, [
    queryName, selectedCategories, searchKeyword, selectedPortfolioIds,
    selectedTagsWatchlistIds, filterMode, keywordOp, keywordWatchlistOp,
    notifications, isEditing, options.queryId, hasKeywords, keywordsSize,
    editMutation, createMutation, options.onSaved, options.onSaveFailed,
  ]);

  const handleDelete = useCallback(async () => {
    if (!options.queryId) return;
    const ok = await confirm({ title: "Delete Filter", message: "This filter will be permanently deleted. This action cannot be undone.", confirmLabel: "Delete", variant: "danger" });
    if (!ok) return;

    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(options.queryId);
      toast.success("Filter deleted");
      options.onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to delete filter");
    } finally {
      setIsDeleting(false);
    }
  }, [options.queryId, deleteMutation, options.onDeleted]);

  return {
    // Form state
    queryName, setQueryName,
    searchKeyword, setSearchKeyword,
    selectedPortfolioIds, setSelectedPortfolioIds,
    selectedCategories, setSelectedCategories,
    selectedTagsWatchlistIds, setSelectedTagsWatchlistIds,
    filterMode, setFilterMode,
    keywordOp, setKeywordOp,
    keywordWatchlistOp, setKeywordWatchlistOp,
    notifications, setNotifications,

    // Toggles
    togglePortfolio,
    toggleCategory,
    toggleTagWatchlist,
    clearAll,
    reset,

    // Derived
    isEditing,
    selectedGrouped,
    selectedTagWatchlistObjects,
    semanticSummary,
    keywordsSize,
    hasKeywords,
    hasTickerWatchlists,
    hasTags,
    hasWatchlistsOrTags,
    tickerCount,
    keywordCount,
    tagCount,
    formErrors,

    // Data
    portfolios,
    tagsWatchlists,

    // Mutations
    handleSubmit,
    handleDelete,
    isSubmitting: createMutation.isPending || editMutation.isPending,
    isDeleting,

    // Keyword limits
    MAX_KEYWORDS_COUNT,
    MAX_KEYWORDS_TOTAL_CHARS,
  };
}

export type UseFilterBuilderReturn = ReturnType<typeof useFilterBuilder>;
